import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Check } from 'lucide-react';
type FAQItem = {
  q: string;
  a: React.ReactNode;
};
function CheckList({
  items
}: {
  items: string[];
}) {
  return <ul className="space-y-2 mt-3" data-id="element-53">
      {items.map(item => <li key={item} className="flex items-start gap-2" data-id="element-54">
          <Check className="w-5 h-5 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-55" />
          <span data-id="element-56">{item}</span>
        </li>)}
    </ul>;
}
const FAQS: FAQItem[] = [{
  q: "I don't have artwork. Can you help?",
  a: <>
        <p className="font-bold" data-id="element-57">Yes.</p>
        <p data-id="element-58">
          We can work from business cards, screenshots, sketches, low-resolution
          logos, and even ideas. In many cases, basic artwork cleanup is
          included at no additional charge.
        </p>
        <p data-id="element-59">
          If your logo requires extensive recreation or design work, we'll
          provide a quote before starting.
        </p>
      </>
}, {
  q: 'What is your minimum order?',
  a: <>
        <p data-id="element-60">
          Our minimum order is <span className="font-bold" data-id="element-61">24 pieces</span>.
        </p>
        <p data-id="element-62">
          Perfect for small businesses, contractors, restaurants, schools,
          sports teams, events, and company uniforms.
        </p>
      </>
}, {
  q: 'How much does custom apparel cost?',
  a: <>
        <p data-id="element-63">
          Pricing depends on garment style, quantity, artwork, and decoration
          method.
        </p>
        <p className="font-display font-bold uppercase tracking-wider mt-4" data-id="element-64">
          Sample Pricing
        </p>
        <p className="text-sm text-gray-500" data-id="element-65">
          Medium Weight 100% Cotton Short Sleeve Crew Neck T-Shirt — Single
          Print Location
        </p>
        <ul className="space-y-1 mt-3" data-id="element-66">
          <li data-id="element-67">• 24 Pieces — Starting at $14.50 each</li>
          <li data-id="element-68">• 48 Pieces — Starting at $12.90 each</li>
          <li data-id="element-69">• 72 Pieces — Starting at $11.90 each</li>
          <li data-id="element-70">• 144 Pieces — Starting at $9.90 each</li>
        </ul>
        <p className="font-bold mt-4" data-id="element-71">Includes:</p>
        <CheckList items={['Medium Weight 100% Cotton T-Shirt', 'Single Print Location', 'Professional DTF Transfer', 'Printed In-House', 'Basic Artwork Setup']} data-id="element-72" />
        <p className="text-sm text-gray-500 italic mt-4" data-id="element-73">
          Pricing shown as an example only. Final pricing may vary depending on
          garment selection, artwork requirements, and print specifications.
        </p>
      </>
}, {
  q: 'What is your turnaround time?',
  a: <>
        <p data-id="element-74">
          Most orders are completed within{' '}
          <span className="font-bold" data-id="element-75">3–5 business days</span> after artwork
          approval.
        </p>
        <p data-id="element-76">
          Rush services may be available depending on our production schedule.
        </p>
      </>
}, {
  q: 'Do you charge for shipping?',
  a: <>
        <p data-id="element-77">
          Most orders over <span className="font-bold" data-id="element-78">$200</span> qualify for
          FREE Standard Economy Shipping anywhere in the United States.
        </p>
        <p data-id="element-79">
          Need your order faster? Expedited shipping options are available upon
          request.
        </p>
      </>
}, {
  q: 'Do you offer local pickup?',
  a: <>
        <p className="font-bold" data-id="element-80">Yes.</p>
        <p data-id="element-81">
          Local pickup is available in San Diego, California. We also ship
          nationwide throughout the United States.
        </p>
      </>
}, {
  q: 'Can I mix sizes?',
  a: <>
        <p className="font-bold" data-id="element-82">Absolutely.</p>
        <p data-id="element-83">You can mix sizes within the same order at no additional charge.</p>
      </>
}, {
  q: 'What products do you print?',
  a: <>
        <p data-id="element-84">We offer:</p>
        <CheckList items={['T-Shirts', 'Long Sleeve Shirts', 'Hoodies', 'Sweatshirts', 'Polo Shirts', 'Safety Vests', 'Hats', 'Team Apparel', 'Company Uniforms', 'Event Merchandise', 'Promotional Apparel']} data-id="element-85" />
      </>
}, {
  q: 'Do you print in-house?',
  a: <>
        <p className="font-bold" data-id="element-86">Yes.</p>
        <p data-id="element-87">
          All apparel is printed in-house at our San Diego facility using
          professional production equipment.
        </p>
        <p className="font-bold" data-id="element-88">No brokers. No outsourcing. No middlemen.</p>
      </>
}, {
  q: "I'm not sure what I need. Can you help?",
  a: <>
        <p className="font-bold" data-id="element-89">Absolutely.</p>
        <p data-id="element-90">
          Tell us about your business, event, team, or project and we'll
          recommend the best apparel options based on your budget, quantity, and
          deadline.
        </p>
      </>
}, {
  q: 'How fast do you respond to quote requests?',
  a: <>
        <p data-id="element-91">
          Most quote requests submitted during business hours receive a response
          within a few hours.
        </p>
        <p data-id="element-92">
          For larger or more complex projects, we may contact you to gather
          additional details before providing pricing.
        </p>
      </>
}, {
  q: 'Do you ship outside of California?',
  a: <>
        <p className="font-bold" data-id="element-93">Yes.</p>
        <p data-id="element-94">
          We serve businesses and organizations throughout the United States.
          Orders can be shipped directly to your business, event location, or
          job site.
        </p>
      </>
}, {
  q: "Can you match a competitor's quote?",
  a: <>
        <p className="font-bold" data-id="element-95">In many cases, yes.</p>
        <p data-id="element-96">
          If you have a quote from another provider, send it to us and we'll
          review it. We may be able to match or improve the value depending on
          the project specifications.
        </p>
      </>
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  function toggle(index: number) {
    setOpenIndex(current => current === index ? null : index);
  }
  return <section id="faq" className="scroll-mt-24 py-24 bg-white border-t-4 border-black" data-id="element-97">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-98">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-99">
          <h2 className="text-4xl md:text-5xl font-black mb-4 uppercase" data-id="element-100">
            Frequently Asked Questions
          </h2>
          <div className="w-24 h-2 bg-black mx-auto" data-id="element-101"></div>
        </motion.div>

        <div className="border-t-2 border-black" data-id="element-102">
          {FAQS.map((faq, index) => {
          const isOpen = openIndex === index;
          return <div key={faq.q} className="border-b-2 border-black" data-id="element-103">
                <h3 data-id="element-104">
                  <button onClick={() => toggle(index)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-4 text-left py-5 group" data-id="element-105">
                    <span className="font-display font-bold uppercase tracking-wide text-lg md:text-xl group-hover:text-gray-600 transition-colors" data-id="element-106">
                      {faq.q}
                    </span>
                    <motion.span animate={{
                  rotate: isOpen ? 45 : 0
                }} transition={{
                  duration: 0.2
                }} className="flex-shrink-0 w-8 h-8 bg-black text-white flex items-center justify-center" data-id="element-107">
                      <Plus className="w-5 h-5" strokeWidth={3} data-id="element-108" />
                    </motion.span>
                  </button>
                </h3>
                <AnimatePresence initial={false} data-id="element-109">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-110">
                      <div className="pb-6 pr-12 space-y-3 text-gray-700 font-medium leading-relaxed" data-id="element-111">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </section>;
}